import * as React from 'react';
import axios from 'axios';
import {
  AccessType,
  BuildJsonType,
  EnhancedAppointment,
} from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import swal from 'sweetalert2';
import { AppointmentActionModalContext } from 'contexts';
import { getInternalShowingDetailsUrl } from 'utils';
import { AppointmentActionModalContextType } from 'contexts/modal/types';
import { Listing } from 'api/ListingController/getListing';
import { getServerDate } from './APIService';
import {
  AppointmentInstructions,
  AppointmentDetailsSection,
} from '../../DashboardComponents/AppointmentDetailViewComponents';
import CoopCancelAppointmentComponent, {
  CoopCancelReasonType,
} from '../../DashboardComponents/CoopCancelAppointmentComponent';
import CoopCalendarSyncButton from './CoopCalendarSyncButton';
import CovidSignRequired from '../../covid19/CovidSignRequired';
import AngularLocationServiceContext from './AngularLocationServiceContext';
import { Loading } from '../../react-common';

type AppointmentDetailContainerProps = {
  appointment: BuildJsonType<EnhancedAppointment>;
  listing?: Pick<Listing, 'id' | 'line1' | 'line2' | 'city' | 'org'>;
  updateAppointment: (id: string) => void;
  rescheduleFromTour?: () => void;
  cancelFromTour?: (
    reason: CoopCancelReasonType,
    details: string,
  ) => Promise<void>;
  acceptSuggestionFromTour?: () => Promise<void>;
  denySuggestionFromTour?: () => void;
};

type AppointmentDetailContainerState = {
  serverDate?: Date;
  pageMode: 'default' | 'cancel';
  isLoading: boolean;
};

class AppointmentDetailContainer extends React.Component<
  AppointmentDetailContainerProps,
  AppointmentDetailContainerState
> {
  static contextType = AngularLocationServiceContext;

  declare context: React.ContextType<typeof AngularLocationServiceContext>;

  constructor(props: AppointmentDetailContainerProps) {
    super(props);

    this.state = {
      pageMode: 'default',
      isLoading: false,
    };
  }

  async componentDidMount() {
    const serverDate = await getServerDate();

    this.setState({
      serverDate,
    });
  }

  public coopAcceptTimeChange = async () => {
    const { appointment, updateAppointment } = this.props;
    const { $location, $scope, ToastService } = this.context;

    this.setState({
      isLoading: true,
    });

    try {
      await axios.post(`/user/coop/appointments/${appointment.id}/status`, {
        status: 'confirm',
      });

      return $scope.$apply(() => {
        $location.path(`/my_business/showings/buyer`);
        $location.search('toast', 'showing_confirmed');
      });
    } catch (err) {
      errorReporter.error(err);
      this.setState({
        isLoading: false,
      });
      $scope.$apply(() => {
        ToastService.add({
          type: 'danger',
          message: 'Failed to confirm the suggested time change',
        });
      });
    }
  };

  public coopDenyTimeChange = async (
    _appointmentActionModalContext?: AppointmentActionModalContextType,
  ) => {
    if (_appointmentActionModalContext && this.props.appointment)
      return _appointmentActionModalContext.openModal({
        agent: 'coop',
        action: 'deny_coop',
        // @ts-ignore
        appointment: this.props.appointment,
        onConfirm: () =>
          this.context.$location.path(`/my_business/showings/buyer`),
      });

    const { appointment, updateAppointment } = this.props;
    const { $location, $scope, ToastService } = this.context;

    await swal.fire({
      title: 'Deny Suggestion',
      text: 'Are you sure you want to Deny this time change suggestion?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#db5e42',
      confirmButtonText: 'Yes',
      preConfirm: async () => {
        this.setState({
          isLoading: true,
        });

        try {
          await axios.post(`/appointments/send/${appointment.id}/deny_coop`, {
            notify: appointment.notify,
            isPending: false, // TODO: confirm with costa
          });
          updateAppointment(appointment.id);
          this.setState({ isLoading: false });
          $scope.$apply(() => {
            ToastService.add({
              type: 'success',
              message: 'Time Change Suggestion Denied',
            });
            $location.path(getInternalShowingDetailsUrl(appointment.id));
          });
        } catch (err) {
          errorReporter.error(err);
          this.setState({ isLoading: false });
          $scope.$apply(() => {
            ToastService.add({
              type: 'danger',
              message: 'Failed to deny the suggested time change',
            });
          });
        }
      },
    });
  };

  render() {
    const { serverDate, pageMode, isLoading } = this.state;
    const {
      appointment,
      updateAppointment,
      rescheduleFromTour,
      cancelFromTour,
      acceptSuggestionFromTour,
      denySuggestionFromTour,
      listing,
    } = this.props;

    if (!serverDate) return null;
    if (isLoading) return <Loading size="fa-5x" />;

    const { appointmentConfig, lastAction, status, dynamicLockBoxCode } =
      appointment;

    let actionDetails: any;
    if (status === 'confirm' && appointmentConfig) {
      actionDetails = appointmentConfig;
    } else if (lastAction) {
      actionDetails = lastAction.actionDetails;
    }

    const rescheduleProps = rescheduleFromTour
      ? {
          onClick: rescheduleFromTour,
        }
      : {
          href: `#/my_business/appointment/${appointment.id}/time_change${
            this.context.$location.search().redirectTo === 'calendar'
              ? '?redirectTo=calendar'
              : ''
          }`,
        };

    return (
      <AppointmentActionModalContext.Consumer>
        {appointmentActionModalContext => (
          <>
            <div style={{ display: 'flex' }}>
              <h2 className="font-light m-b-xs">{`${
                appointment.listing.line1
              } ${appointment.listing.line2 || ''} - Showing Details`}</h2>
            </div>
            <hr style={{ marginTop: '10px', marginBottom: '10px' }} />
            <div style={{ width: '100%' }}>
              <div className="row">
                <div className="col-md-6 m-b-sm">
                  <AppointmentDetailsSection
                    appointment={appointment}
                    calendarSyncButton={
                      serverDate < new Date(appointment.time) &&
                      appointment.status === 'confirm' && (
                        <CoopCalendarSyncButton appointment={appointment} />
                      )
                    }
                    $rootScope={this.context.$rootScope}
                  />
                  {appointment.status === 'suggested_time_change' &&
                    !appointment.keepOriginalAppointment && (
                      <div
                        style={{
                          margin: '0 0 20px 10px',
                          textTransform: 'initial',
                        }}
                      >
                        If you deny this suggestion,{' '}
                        <span className="attention">
                          your original showing will also be cancelled.
                        </span>
                      </div>
                    )}
                  {pageMode === 'default' && (
                    <div className="row appointment-change-buttons">
                      {serverDate < new Date(appointment.time) &&
                        !['cancel', 'deny', 'suggested_time_change'].includes(
                          appointment.status,
                        ) && (
                          <a
                            className="btn btn-danger btn-sm"
                            data-cy="cancel"
                            onClick={
                              cancelFromTour
                                ? () => this.setState({ pageMode: 'cancel' })
                                : () => {
                                    appointmentActionModalContext.openModal({
                                      agent: 'coop',
                                      action: 'cancel',
                                      // @ts-ignore
                                      appointment,
                                      onConfirm: () => {
                                        this.context.$location.search(
                                          'redirectTo',
                                          null,
                                        );
                                        this.context.$location.search(
                                          'wait',
                                          'LARGE',
                                        );
                                        this.context.$location.path(
                                          '/my_business/showings/buyer',
                                        );
                                      },
                                    });
                                  }
                            }
                          >
                            CANCEL SHOWING
                          </a>
                        )}
                      {(serverDate < new Date(appointment.time) ||
                        appointment.status !== 'confirm') &&
                        appointment.status !== 'suggested_time_change' && (
                          <a
                            className="btn btn-primary btn-sm"
                            data-cy="reschedule"
                            {...rescheduleProps}
                          >
                            RESCHEDULE
                          </a>
                        )}
                      {serverDate < new Date(appointment.time) &&
                        appointment.status === 'suggested_time_change' && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <a
                              className="btn btn-success"
                              style={{
                                textTransform: 'capitalize',
                                marginRight: '10px',
                              }}
                              onClick={
                                acceptSuggestionFromTour ||
                                this.coopAcceptTimeChange
                              }
                            >
                              Accept
                            </a>
                            <a
                              className="btn btn-danger"
                              style={{ textTransform: 'capitalize' }}
                              onClick={
                                denySuggestionFromTour ||
                                (() =>
                                  this.coopDenyTimeChange(
                                    appointmentActionModalContext,
                                  ))
                              }
                            >
                              Deny
                            </a>
                          </div>
                        )}
                      {serverDate >= new Date(appointment.time) &&
                        appointment.status === 'confirm' &&
                        [
                          'Buyer/Broker',
                          'Revisit',
                          'Dual Agency',
                          'Agent Inspection',
                        ].includes(appointment.type) &&
                        !appointment.feedbackCollected && (
                          <a
                            className="btn btn-default btn-sm"
                            href={`#/my_business/appointment/${appointment.id}/feedback`}
                          >
                            Leave Feedback
                          </a>
                        )}
                      {appointment.feedbackCollected && (
                        <button
                          className="btn btn-success btn-sm btn-outline"
                          style={{ pointerEvents: 'none' }}
                        >
                          Feedback Submitted
                        </button>
                      )}
                    </div>
                  )}
                  {pageMode === 'cancel' && (
                    <div>
                      <CoopCancelAppointmentComponent
                        setCancelStatus={cancelFromTour}
                        resetPageMode={() =>
                          this.setState({ pageMode: 'default' })
                        }
                      />
                    </div>
                  )}
                </div>

                {listing.org.features.includes('COVID_19') && (
                  <CovidSignRequired
                    appointment={appointment}
                    isInternal
                    updateAppointment={updateAppointment}
                  />
                )}
                {appointment.status === 'confirm' &&
                  !appointment.isPending &&
                  !!actionDetails && (
                    <div className="col-md-6">
                      <AppointmentInstructions
                        accessType={
                          (actionDetails as { access: AccessType }).access
                        }
                        instructions={(actionDetails as any).instructions}
                        dynamicLockBoxCode={dynamicLockBoxCode}
                      />
                    </div>
                  )}
              </div>
            </div>
          </>
        )}
      </AppointmentActionModalContext.Consumer>
    );
  }
}

export default AppointmentDetailContainer;
